import { useState } from 'react'

import styled from 'styled-components'

import {
  SiteWalkStitchStatus,
  enqueueSiteWalkStitching,
  fetchSiteWalkStitchStatus,
} from '../../../../api/insta360Stitching'
import { useNotifications } from '../../../../contexts/notificationProvider'

const Section = styled.div`
  background: #ffffff;
  border: 1px solid #e6e8ec;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 20px;
`

const SectionTitle = styled.h2`
  margin: 0 0 12px;
`

const SectionSubtitle = styled.p`
  margin: 0 0 20px;
  color: #666;
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
`

const Label = styled.label`
  display: block;
  font-size: 12px;
  color: #4a4a4a;
  margin-bottom: 8px;
`

const TextInput = styled.input`
  width: 100%;
  border: 1px solid #d5d7dc;
  border-radius: 6px;
  padding: 10px;
  font-size: 14px;
  box-sizing: border-box;
`

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;
`

const ActionButton = styled.button<{ $primary?: boolean }>`
  border: 1px solid ${props => (props.$primary ? '#0a4f95' : '#ccd2db')};
  background: ${props => (props.$primary ? '#0a4f95' : '#fff')};
  color: ${props => (props.$primary ? '#fff' : '#465265')};
  border-radius: 6px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const StatusPanel = styled.pre`
  background: #141821;
  color: #e6ecff;
  padding: 12px;
  border-radius: 8px;
  font-size: 12px;
  margin-top: 16px;
  white-space: pre-wrap;
`

const HelpText = styled.p`
  margin: 16px 0 0;
  font-size: 13px;
  color: #666;
  line-height: 1.5;
`

export const Insta360StitchEnqueueTool = () => {
  const [projectPublicId, setProjectPublicId] = useState('')
  const [siteWalkId, setSiteWalkId] = useState('')
  const [status, setStatus] = useState<SiteWalkStitchStatus | null>(null)
  const [isLoadingStatus, setIsLoadingStatus] = useState(false)
  const [isEnqueueing, setIsEnqueueing] = useState(false)
  const { addNotification } = useNotifications()

  const parsedSiteWalkId = Number.parseInt(siteWalkId, 10)
  const scopeReady =
    projectPublicId.trim().length > 0 &&
    Number.isFinite(parsedSiteWalkId) &&
    parsedSiteWalkId > 0

  const handleCheckStatus = async () => {
    if (!scopeReady) {
      return
    }
    setIsLoadingStatus(true)
    try {
      const result = await fetchSiteWalkStitchStatus(
        projectPublicId.trim(),
        parsedSiteWalkId,
      )
      setStatus(result)
    } catch (error) {
      addNotification(
        'Failed to load stitch status for this site walk.',
        'error',
      )
    } finally {
      setIsLoadingStatus(false)
    }
  }

  const handleEnqueue = async () => {
    if (!scopeReady) {
      return
    }
    setIsEnqueueing(true)
    try {
      const result = await enqueueSiteWalkStitching(
        projectPublicId.trim(),
        parsedSiteWalkId,
      )
      setStatus({
        pending: result.pending,
        complete: result.complete,
        failed_heuristic: result.failed_heuristic,
        total: result.total,
        site_walk_id: result.site_walk_id,
        project_public_id: result.project_public_id,
      })
      addNotification(
        `Enqueued ${result.enqueued} stitch task(s) for site walk ${result.site_walk_id}.`,
        'success',
      )
    } catch (error) {
      addNotification(
        'Failed to enqueue stitching. Check worker broker and stitching queue.',
        'error',
      )
    } finally {
      setIsEnqueueing(false)
    }
  }

  return (
    <Section>
      <SectionTitle>Insta360 stitching</SectionTitle>
      <SectionSubtitle>
        Re-queue Celery stitch jobs for one site walk. Requires project public
        id and site walk id. Normal uploads enqueue per image automatically (not
        project-scoped).
      </SectionSubtitle>

      <Grid>
        <div>
          <Label htmlFor="insta360-project-id">Project public id</Label>
          <TextInput
            id="insta360-project-id"
            value={projectPublicId}
            onChange={e => setProjectPublicId(e.target.value)}
            placeholder="e.g. VxcjW2JukTRf"
          />
        </div>
        <div>
          <Label htmlFor="insta360-site-walk-id">Site walk id</Label>
          <TextInput
            id="insta360-site-walk-id"
            value={siteWalkId}
            onChange={e => setSiteWalkId(e.target.value)}
            placeholder="e.g. 5452"
            inputMode="numeric"
          />
        </div>
      </Grid>

      <ButtonRow>
        <ActionButton
          type="button"
          disabled={!scopeReady || isLoadingStatus}
          onClick={handleCheckStatus}
        >
          {isLoadingStatus ? 'Checking…' : 'Check status'}
        </ActionButton>
        <ActionButton
          type="button"
          $primary
          disabled={!scopeReady || isEnqueueing}
          onClick={handleEnqueue}
        >
          {isEnqueueing ? 'Enqueueing…' : 'Enqueue stitching'}
        </ActionButton>
      </ButtonRow>

      {status ? (
        <StatusPanel>{JSON.stringify(status, null, 2)}</StatusPanel>
      ) : null}

      <HelpText>
        <code>requires_stitching=1</code> in the database is not the Celery
        queue. Workers must listen to the <code>stitching</code> queue with CPU
        MediaSDK settings. See nextera-api{' '}
        <code>docs/insta360-stitching-ops.md</code>.
      </HelpText>
    </Section>
  )
}
